import * as React from 'react';
import { ActionPlanRiskConsequence, ActionPlanRiskLikelihood } from '../../amorg-common';
import { SegmentBar } from './SegmentBar';
import { riskAssessment } from '../../utils/riskAssessment';
import { Flex } from '../compositeComponents/FlexHelpers';
import { colors } from '../colors/color';
import { Label } from '../Label';
import { Span12 } from '../typography/Body';
import { txt } from '../../txt';
import { DetailedRiskAssessmentModalButton } from '../../pages/actionplans/components/DetailedRiskAssessmentModalButton';
import { DialGauge } from './DialGauge';

type RiskAssessmentEvaluatorProps = {
    consequence: ActionPlanRiskConsequence,
    likelihood: ActionPlanRiskLikelihood,
    onChange: (consequence: ActionPlanRiskConsequence, likelihood: ActionPlanRiskLikelihood) => void;
};
export function RiskAssessmentEvaluator (props: RiskAssessmentEvaluatorProps): JSX.Element
{

    const risk = riskAssessment.calculateRisk(props.consequence, props.likelihood);
    const riskColor = colors.riskCategory(risk);

    return (
        // Remove width to enable scaling segments in segmentbar
        <Flex flexDirection="column">
            
            <Flex gap={4}>
                <Flex flexDirection="column" justifyContent='flex-end' alignItems='center' gap={8} style={{width: '110px'}}>
                        <DialGauge
                            segments={4}
                            selectedSegments={riskAssessment.getRiskIndex(risk) + 1}
                            color={riskColor}
                            widthPx={60}
                        />
                        <Span12>{risk}</Span12>
                </Flex>

                <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '2px'}}>

                    <Flex justifyContent='center' alignItems='center' gap={4}>
                        <Label style={{alignSelf:'center'}}>Risikovurdering</Label>
                        <DetailedRiskAssessmentModalButton
                            consequence={props.consequence}
                            likelihood={props.likelihood}
                            onUpdate={(consequence, likelihood) => props.onChange(consequence, likelihood)} />
                    </Flex>


                    <Flex flexDirection="column">
                        <LabelledSegmentBar
                            label={txt.riskAssessment.likelihood(props.likelihood)}
                            currentValue={riskAssessment.getLikelihoodIndex(props.likelihood) as 0 | 1 | 2 | 3}
                            onClick={(value) => props.onChange(props.consequence, riskAssessment.getLikelihoodFromIndex(value))}
                            tooltips={{
                                t0:txt.riskAssessment.likelihood('1 - unlikely'),
                                t1:txt.riskAssessment.likelihood('2 - rare'),
                                t2:txt.riskAssessment.likelihood('3 - possible'),
                                t3:txt.riskAssessment.likelihood('4 - likely')
                            }}
                        />

                        <LabelledSegmentBar
                            label={txt.riskAssessment.consequence(props.consequence)}
                            currentValue={riskAssessment.getConsequenceIndex(props.consequence) as 0 | 1 | 2 | 3}
                            onClick={(value) => props.onChange(riskAssessment.getConsequenceFromIndex(value), props.likelihood)}
                            tooltips={{
                                t0:txt.riskAssessment.consequence('1 - small'),
                                t1:txt.riskAssessment.consequence('2 - significant'),
                                t2:txt.riskAssessment.consequence('3 - severe'),
                                t3:txt.riskAssessment.consequence('4 - catastrophic')
                            }}
                        />
                    </Flex>
                </div>
            </Flex>
        </Flex>
    );
}


function LabelledSegmentBar(props: { 
    label: string,
    currentValue: 0 | 1 | 2 | 3,
    tooltips:{
        t0: string,
        t1: string,
        t2: string,
        t3: string
    },
    onClick:(value:0|1|2|3) => void
}) {

    const selectedColor = colors.graphs.segmentBar.selected;
    const neutralColor = colors.graphs.segmentBar.default;

    return (
        <Flex justifyContent="flex-end" style={{flex:"1 0 0"}} gap={8}>
            <Span12 style={{minWidth:"160px", textAlign:"right"}}>{props.label}</Span12>
            <SegmentBar totalSegments={4} height={16} minWidthPx={80} segments={[
                {
                    color: selectedColor,
                    onClick: () => props.onClick(0),
                    toolTip: <span>{props.tooltips.t0}</span>
                },

                {
                    color: props.currentValue >= 1 ? selectedColor : neutralColor,
                    onClick: () => props.onClick(1),
                    toolTip: <span>{props.tooltips.t1}</span>
                },

                {
                    color: props.currentValue >= 2 ? selectedColor : neutralColor,
                    onClick: () => props.onClick(2),
                    toolTip: <span>{props.tooltips.t2}</span>
                },
                {
                    color: props.currentValue >= 3 ? selectedColor : neutralColor,
                    onClick: () => props.onClick(3),
                    toolTip: <span>{props.tooltips.t3}</span>
                }]}
            />
        </Flex>
    );
}
    